import {
  Button,
  DatePicker,
  EchartsWithTheme,
  Empty,
  Form,
  message,
  Modal,
  Select,
  useTheme,
} from '@maxtropy/components';
import { Spin } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { apiV2LabelDetailChartPost } from '@maxtropy/device-customer-apis-v2';
import { getModalChartOption } from './chart';
import { RangeValue } from '../..';
import { DateType, getDefaultTime } from '@/pages/EnergyLabel/utils';
import { DataPeriodListItem, getDataQueryPeriodList } from '@/api/energyOverview';
import { ListItemProps } from '.';

export interface EnergyData extends ListItemProps {
  energyMediumId: number;
}

export interface IShowDetailModal {
  onCancel?: () => void;
  timeResolution?: DateType;
  selectedConsumption?: number;
  displayValue?: RangeValue;
  dataQueryPeriodId?: number;
  energyData: ListItemProps;
}

const ShowDetailModal: FC<IShowDetailModal> = props => {
  const {
    energyData,
    onCancel,
    timeResolution,
    selectedConsumption,
    displayValue: outDisplayValue,
    dataQueryPeriodId: outDataQueryPeriodId,
  } = props;
  const theme = useTheme();
  const [displayValue, setDisplayValue] = useState<RangeValue>();
  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number>();
  const [dataPeriodList, setDataPeriodList] = useState<DataPeriodListItem[]>([]);

  useEffect(() => {
    setDisplayValue(outDisplayValue);
    setDataQueryPeriodId(outDataQueryPeriodId);
  }, []);

  useEffect(() => {
    getDataQueryPeriodList({
      startTime: getDefaultTime(timeResolution!, displayValue!).startTime,
      endTime: getDefaultTime(timeResolution!, displayValue!).endTime,
    }).then(res => {
      setDataPeriodList(res.dataPeriodList ?? []);
    });
  }, [timeResolution, displayValue]);

  // 获取图表
  const { data, loading } = useRequest(
    () =>
      apiV2LabelDetailChartPost({
        energyUnitId: energyData.energyUnitId,
        startTime: getDefaultTime(timeResolution!, displayValue!).startTime,
        endTime: getDefaultTime(timeResolution!, displayValue!).endTime,
        dataQueryPeriodId,
        energyMediumId: selectedConsumption,
        timeResolution: timeResolution,
      }).then(res => {
        return res;
      }),
    {
      ready: !!energyData && !!displayValue && !!timeResolution && !!selectedConsumption,
      refreshDeps: [energyData, displayValue, timeResolution, selectedConsumption, dataQueryPeriodId],
    }
  );

  // render picker
  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  // x轴
  const xLabel = useMemo(() => {
    if (timeResolution === DateType.MONTH || timeResolution === DateType.WEEK) {
      return 'YYYY-MM-DD';
    } else if (timeResolution === DateType.YEAR) {
      return 'YYYY-MM';
    } else {
      return 'YYYY-MM-DD HH:mm';
    }
  }, [timeResolution]);

  const disabledDate = (current: Dayjs) => {
    if (timeResolution === DateType.DAY) {
      return current && current >= dayjs().startOf('day');
    } else {
      let tooLate = null;
      let tooEarly = null;
      const afterToday = current && current > dayjs().endOf('day');
      if (DateType.MONTH === timeResolution) {
        tooLate = displayValue?.[0] && current.diff(displayValue?.[0], 'months') >= 23;
        tooEarly = displayValue?.[1] && displayValue?.[1].diff(current, 'months') >= 23;
      }
      return !!tooEarly || !!tooLate || !!afterToday;
    }
  };

  const onChangePicker = (dates: RangeValue) => {
    switch (timeResolution) {
      case DateType.DAY:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(30, 'day').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择31天');
          return;
        }
        break;
      case DateType.WEEK:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(10, 'week').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择10周');
          return;
        }
        break;
      case DateType.MONTH:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(24, 'month').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择24个月');
          return;
        }
        break;
      case DateType.YEAR:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(6, 'year').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择6年');
          return;
        }
        break;
    }
    setDataQueryPeriodId?.(undefined);
    setDisplayValue(dates);
  };

  // 查询图表
  const chartOption = useMemo(
    () =>
      getModalChartOption(
        xLabel,
        theme,
        timeResolution!,
        { ...energyData, energyMediumId: selectedConsumption! },
        data
      ),
    [data, xLabel]
  );

  return (
    <Modal
      title={`详情`}
      size="big"
      open={true}
      bodyScroll
      onCancel={onCancel}
      footer={[
        <Button key={'cancel'} onClick={onCancel}>
          关闭
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className={styles.filter_box}>
          <Form.Item label="时间范围">
            <DatePicker.RangePicker
              picker={picker}
              style={{ width: '250px' }}
              value={displayValue}
              disabledDate={disabledDate}
              onChange={onChangePicker}
              allowClear={false}
            />
          </Form.Item>
          <Form.Item label="数据查询时段">
            <Select
              placeholder="请选择"
              style={{ width: 150 }}
              value={dataQueryPeriodId}
              onChange={(value: number) => setDataQueryPeriodId(value)}
              options={dataPeriodList.map(item => ({ value: item.id, label: item.name }))}
              allowClear
            />
          </Form.Item>
        </div>

        {!data?.chartDetail ? (
          <Empty className={styles.modal_empty_box} />
        ) : (
          <EchartsWithTheme theme={theme} style={{ width: '100%', height: 450 }} option={chartOption} />
        )}
      </Spin>
    </Modal>
  );
};

export default ShowDetailModal;
